// Pure helpers for the multi-target variation model. Variations carry a
// `targets[]` list — zero-or-one contract entry plus zero-or-more SOW
// entries. Each target can optionally hold an `allocation` (a portion of
// the variation's `valueImpact`). When any target carries an allocation
// they all must, and the sum must equal `valueImpact`.
//
// `legacyToTargets` synthesises a single-element list from the legacy
// `variesType` + `variesId` shape. `readTargets` is the canonical reader
// — it prefers a stored `targets[]` and falls back to the synthesis only
// when absent. Component code should never read `variesType` /
// `variesId` directly.

import type { Variation, VariationTarget } from "../../../../types";

interface LegacyShape {
  variesType?: Variation["variesType"];
  variesId?: Variation["variesId"];
}

/** Synthesise a single-target list from the legacy `variesType` /
 *  `variesId` fields. Defaults to a contract target when neither is
 *  populated — that matches the backend's legacy default for orphan
 *  records and keeps the read path safe. */
export function legacyToTargets(legacy: LegacyShape): VariationTarget[] {
  if (legacy.variesType === "sow" && legacy.variesId) {
    return [{ kind: "sow", sowId: legacy.variesId }];
  }
  return [{ kind: "contract" }];
}

/** Canonical target reader. Prefer `variation.targets`; fall back to
 *  synthesising from the deprecated legacy fields only when absent. */
export function readTargets(variation: Variation): VariationTarget[] {
  if (variation.targets && variation.targets.length > 0) {
    return variation.targets;
  }
  return legacyToTargets({
    variesType: variation.variesType,
    variesId: variation.variesId,
  });
}

/** Sum the allocations across all targets, treating null/undefined as 0. */
export function allocationSum(targets: VariationTarget[]): number {
  return targets.reduce((acc, t) => acc + (t.allocation ?? 0), 0);
}

/** True when at least one target carries a non-null allocation — used to
 *  decide whether to surface the live "Allocations sum: $X / $Y" footer. */
export function hasAnyAllocation(targets: VariationTarget[]): boolean {
  return targets.some(
    (t) => t.allocation !== null && t.allocation !== undefined,
  );
}

function fmtAud(n: number): string {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}$${abs}`;
}

/** Validate the target list against the variation's total `valueImpact`.
 *  Returns null on success, otherwise a user-facing error message.
 *
 *  Rules:
 *    - At least one target must be selected.
 *    - On a non-financial variation (valueImpact === null) no target may
 *      carry an allocation.
 *    - If any target carries an allocation, every target must.
 *    - The sum of allocations must equal `valueImpact`. */
export function validateAllocations(
  targets: VariationTarget[],
  valueImpact: number | null,
): string | null {
  if (targets.length === 0) {
    return "Select at least one target.";
  }
  const anyAllocated = hasAnyAllocation(targets);
  if (valueImpact === null) {
    if (anyAllocated) {
      return "Allocations cannot be set on a non-financial variation.";
    }
    return null;
  }
  if (!anyAllocated) {
    return null;
  }
  const allAllocated = targets.every(
    (t) => t.allocation !== null && t.allocation !== undefined,
  );
  if (!allAllocated) {
    return "Set an allocation for every selected target, or clear all allocations.";
  }
  const sum = allocationSum(targets);
  // Compare in cents to avoid IEEE 754 noise — 0.1 + 0.2 sums to
  // 0.30000000000000004 in JS, which would falsely reject a valid split.
  const sumCents = Math.round(sum * 100);
  const targetCents = Math.round(valueImpact * 100);
  if (sumCents !== targetCents) {
    return `Allocations sum to ${fmtAud(sum)}; must equal ${fmtAud(valueImpact)}.`;
  }
  return null;
}

/** Toggle the contract target in/out of the list. Adds a fresh
 *  `{ kind: 'contract' }` (no allocation) when absent; removes it
 *  otherwise. SOW targets are untouched. */
export function toggleContractTarget(
  targets: VariationTarget[],
): VariationTarget[] {
  const idx = targets.findIndex((t) => t.kind === "contract");
  if (idx === -1) {
    return [...targets, { kind: "contract" }];
  }
  return targets.filter((t) => t.kind !== "contract");
}

/** Toggle a specific SOW target in/out of the list by sowId. */
export function toggleSowTarget(
  targets: VariationTarget[],
  sowId: string,
): VariationTarget[] {
  const idx = targets.findIndex(
    (t) => t.kind === "sow" && t.sowId === sowId,
  );
  if (idx === -1) {
    return [...targets, { kind: "sow", sowId }];
  }
  return targets.filter(
    (t) => !(t.kind === "sow" && t.sowId === sowId),
  );
}

function targetMatches(a: VariationTarget, b: VariationTarget): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "contract") return true;
  return a.sowId === (b as { kind: "sow"; sowId: string }).sowId;
}

/** Set (or clear, when value is null) the allocation on a single target.
 *  No-op when the matching target is not present. */
export function setTargetAllocation(
  targets: VariationTarget[],
  match: VariationTarget,
  value: number | null,
): VariationTarget[] {
  return targets.map((t) =>
    targetMatches(t, match) ? { ...t, allocation: value } : t,
  );
}

/** When a target list reduces to a single entry, the partition UI is
 *  suppressed (100% to the lone target is implicit). Drop any leftover
 *  allocation on that target so a) form validation doesn't trip on a
 *  hidden mismatch with `valueImpact` and b) the wire payload reflects
 *  the same "unallocated" state the user sees. No-op for empty or
 *  multi-target lists. */
export function clearAllocationsIfSingle(
  targets: VariationTarget[],
): VariationTarget[] {
  if (targets.length !== 1) return targets;
  const t = targets[0];
  if (t.allocation === null || t.allocation === undefined) return targets;
  return [
    t.kind === "contract"
      ? { kind: "contract" }
      : { kind: "sow", sowId: t.sowId },
  ];
}

/** Strip `allocation` fields from every target. Used on the write path
 *  when the variation is non-financial — allocations are nonsensical
 *  there, and the backend rejects them. */
export function stripAllocations(targets: VariationTarget[]): VariationTarget[] {
  return targets.map((t) => {
    if (t.kind === "contract") return { kind: "contract" };
    return { kind: "sow", sowId: t.sowId };
  });
}
